import React, { forwardRef, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface FloatingLabelDatepickerProps {
  label?: string;
  value: Date | null;
  disabled?: boolean;
  onChange: (date: Date | null) => void;
  error?: string;
  touched?: boolean;
  required?: boolean;
  style?: React.CSSProperties;
  dateFormat?: string;
  canClear?: boolean;
  showInfo?: boolean;
  infoMessage?: string;
  [key: string]: any; // Allow additional props for DatePicker
}

const FloatingLabelDatepicker: React.FC<FloatingLabelDatepickerProps> = ({
  label,
  value,
  disabled = false,
  onChange,
  error,
  touched,
  required = false,
  style,
  dateFormat = "MM/dd/yyyy",
  canClear,
  maxDate,
  showInfo = false,
  infoMessage = "",
  ...props
}) => {
  const datePickerRef = useRef<DatePicker | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (datePickerRef.current) {
        (datePickerRef.current as any).setOpen(false); // Close DatePicker when scrolling
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);

  const CustomInput = forwardRef<
    HTMLInputElement,
    {
      value: string;
      onClick: () => void;
      onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    }
  >(({ value, onClick, onChange: onInputChange }, ref) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (canClear && e.target.value === "") {
        onChange(null);
      }
      if (onInputChange) {
        onInputChange(e);
      }
    };

    return (
      <div className="form-floating position-relative" style={style}>
        <input
          type="text"
          className={`form-control ${touched && error ? "is-invalid" : ""}`}
          onClick={onClick}
          ref={ref}
          value={value}
          placeholder=" "
          readOnly={!canClear}
          disabled={disabled}
          id="datepicker-input"
          onChange={canClear ? handleInputChange : undefined}
          onKeyDown={(e) => {
            if (!canClear || disabled) return;

            if (e.key === "Backspace" || e.key === "Delete") {
              e.preventDefault();
              onChange(null);
            }
          }}
          style={{
            paddingRight: "40px",
            width: "100%",
            cursor: disabled ? "not-allowed" : "pointer",
            backgroundColor: disabled ? "#e9ecef" : "white",
          }}
        />
        <label htmlFor="datepicker-input">
          {label}
          {!disabled && required && (
            <span className="required" style={{ color: "red" }}>
              *
            </span>
          )}
          {showInfo && !value && (
            <OverlayTrigger
              placement="top"
              trigger={["hover", "focus"]}
              container={document.body}
              overlay={<Tooltip id="info-tooltip">{infoMessage}</Tooltip>}
            >
              <span
                className="material-icons material-symbols-outlined"
                style={{
                  fontSize: "14px",
                  marginLeft: "5px",
                  verticalAlign: "middle",
                  color: "#0d6efd",
                  cursor: "pointer",
                  pointerEvents: "auto",
                  zIndex: 10,
                }}
              >
                info
              </span>
            </OverlayTrigger>
          )}
        </label>

        <span
          className="material-icons material-symbols-outlined date_ic"
          onClick={!disabled ? onClick : undefined}
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: !disabled ? "pointer" : "not-allowed",
            color: "#6c757d",
          }}
        >
          calendar_month
        </span>
        {touched && error && <div className="invalid-feedback">{error}</div>}
      </div>
    );
  });

  CustomInput.displayName = "CustomInput";

  return (
    <div>
      <DatePicker
        ref={datePickerRef}
        selected={value}
        onChange={onChange}
        customInput={React.createElement(CustomInput)}
        maxDate={maxDate}
        dateFormat={dateFormat}
        closeOnScroll={true} // Keep this as an additional safeguard
        peekNextMonth
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        {...props}
      />
    </div>
  );
};

export default FloatingLabelDatepicker;
